/**
 * AdminImagesPage — CRUD for named image slots (SiteImage table).
 *
 * Covers category fallback thumbnails (`category:science`, …) and any
 * other runtime-configurable image URL the app reads by key. Lets admin
 * swap images without a code deploy or re-seed.
 *
 * Conventions:
 *   • `category:<slug>`      — default Course thumbnail when the admin
 *                              hasn't set a custom one on the Course row.
 *   • `hero:<slot>`          — marketing hero fallback.
 *   • `location:<slug>`      — per-location cover photo, future use.
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import ImageUploader from '../../components/ui/image-uploader';
import { Loader2, Pencil, Plus, Trash2, ImageIcon, Search } from 'lucide-react';
import { toast } from 'sonner';

const BLANK = { key: '', label: '', url: '', alt_text: '', notes: '' };

export default function AdminImagesPage() {
  const { api } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/cms/site-images/');
      setImages(Array.isArray(data) ? data : data.results || []);
    } catch {
      toast.error('Failed to load site images');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return images;
    const needle = q.toLowerCase();
    return images.filter(i =>
      i.key?.toLowerCase().includes(needle)
      || i.label?.toLowerCase().includes(needle),
    );
  }, [images, q]);

  const openNew = () => { setEditing(null); setForm(BLANK); setDialogOpen(true); };
  const openEdit = (img) => { setEditing(img); setForm({ ...BLANK, ...img }); setDialogOpen(true); };

  const save = async () => {
    if (!form.key.trim() || !form.url.trim()) {
      toast.error('Key and URL are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/cms/site-images/${editing.id}/`, form);
        toast.success('Image updated');
      } else {
        await api.post('/cms/site-images/', form);
        toast.success('Image created');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.key?.[0] || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (img) => {
    if (!window.confirm(`Delete image slot "${img.key}"? Any courses that fall back to it will lose their image until re-seeded or re-assigned.`)) return;
    try {
      await api.delete(`/cms/site-images/${img.id}/`);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-images-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon className="h-6 w-6" /> Site Images
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Named image slots the app reads at runtime. Edit a slot to change its image everywhere — no re-seed needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8 w-56"
              placeholder="Search key or label"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="site-images-search"
            />
          </div>
          <Button onClick={openNew} data-testid="new-image-btn">
            <Plus className="h-4 w-4 mr-1" /> New slot
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No image slots match your search.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(img => (
            <div
              key={img.id}
              className="border rounded-lg overflow-hidden bg-card flex flex-col"
              data-testid={`image-card-${img.key}`}
            >
              <div className="aspect-video bg-muted overflow-hidden">
                {img.url ? (
                  <img src={img.url} alt={img.alt_text || img.label} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <ImageIcon className="h-10 w-10 opacity-40" />
                  </div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <code className="text-xs font-mono px-2 py-0.5 bg-muted rounded">{img.key}</code>
                  {img.key?.startsWith('category:') && <Badge variant="secondary" className="text-[10px]">fallback</Badge>}
                </div>
                <div className="text-sm font-medium mt-1">{img.label}</div>
                {img.notes && (
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{img.notes}</div>
                )}
                <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                  <Button size="sm" variant="outline" onClick={() => openEdit(img)} data-testid={`edit-image-${img.key}`}>
                    <Pencil className="h-3 w-3 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(img)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="image-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit image slot' : 'New image slot'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Key</Label>
              <Input
                value={form.key}
                disabled={!!editing}
                onChange={e => setForm(p => ({ ...p, key: e.target.value }))}
                placeholder="category:science"
                data-testid="image-key-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Format examples: <code>category:robotics</code>, <code>hero:summer-camps</code>. Once saved the key cannot be edited.
              </p>
            </div>
            <div>
              <Label>Label</Label>
              <Input
                value={form.label}
                onChange={e => setForm(p => ({ ...p, label: e.target.value }))}
                placeholder="Science (category fallback)"
                data-testid="image-label-input"
              />
            </div>
            <div>
              <ImageUploader
                label="Image URL"
                value={form.url}
                onChange={(url) => setForm(p => ({ ...p, url }))}
                folder="site-images"
                testidPrefix="site-image-uploader"
              />
            </div>
            <div>
              <Label>Alt text</Label>
              <Input
                value={form.alt_text}
                onChange={e => setForm(p => ({ ...p, alt_text: e.target.value }))}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-image-btn">
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              {editing ? 'Save changes' : 'Create slot'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
